import { Component, Input, Output, EventEmitter, OnInit, ComponentFactoryResolver } from '@angular/core';

import { CommonService } from '../../../services/common.service';

import { ICompanyContact } from '../../../models/ICompanyContact.model';
import { IScheduleDay } from '../../../models/IScheduleDay.model';
import { IScheduleTime } from '../../../models/IScheduleTime.model';
import { ITimeZone } from '../../../models/ITimeZone.model';
import { IScheduleGroup } from '../../../models/IScheduleGroup.model';

@Component({
  selector: 'app-availability-selector',
  templateUrl: './availabilitySelector.component.html',
  styleUrls: ['./availabilitySelector.component.less']
})
export class AvailabilitySelectorComponent implements OnInit {
  @Input() contact: ICompanyContact;
  @Input() panelMode?: boolean;
  @Output() contactChanged = new EventEmitter();

  days: IWeekDay[] = [
    { SortOrder: 0, ShortName: 'Mon', LongName: 'Monday', Day: 'M', Selected: false },
    { SortOrder: 1, ShortName: 'Tues', LongName: 'Tuesday', Day: 'T', Selected: false },
    { SortOrder: 2, ShortName: 'Wed', LongName: 'Wednesday', Day: 'W', Selected: false },
    { SortOrder: 3, ShortName: 'Thurs', LongName: 'Thursday', Day: 'Th', Selected: false },
    { SortOrder: 4, ShortName: 'Fri', LongName: 'Friday', Day: 'F', Selected: false },
    { SortOrder: 5, ShortName: 'Sat', LongName: 'Saturday', Day: 'S', Selected: false },
    { SortOrder: 6, ShortName: 'Sun', LongName: 'Sunday', Day: 'Su', Selected: false }
  ];

  private daysMap: any;

  hours: { name: string, value: string }[];

  timeZones: ITimeZone[];

  constructor(private common: CommonService) {
    this.daysMap = this.days.reduce((agg, curr) => {
      agg[curr.Day] = curr;
      return agg;
    }, {});
  }

  private generateTimeOptions(): void {
    this.hours = [];

    const initialTime = new Date('1900-01-01T06:00:00');
    const fromMidnight = new Date('1900-01-01T00:00:00');

    let thisTime: Date = initialTime;

    this.hours.push(this.getTimeObject(thisTime));

    for (let i = 0; i < 35; i++) {
      thisTime = new Date(initialTime.setMinutes(initialTime.getMinutes() + 30));
      this.hours.push(this.getTimeObject(thisTime));
    }

    thisTime = fromMidnight;
    this.hours.push(this.getTimeObject(thisTime));

    for (let j = 0; j < 11; j++) {
      thisTime = new Date(fromMidnight.setMinutes(fromMidnight.getMinutes() + 30));
      this.hours.push(this.getTimeObject(thisTime));
    }
  }

  private getTimeObject(time: Date): { name: string, value: string } {
    const hours = time.getHours();
    const minutes = time.getMinutes();

    return {
      name: this.timeToString(time),
      value: `${this.addLeadingZero(hours)}:${this.addLeadingZero(minutes)}:00`
    };
  }

  private addLeadingZero(time: number): string {
    if (time < 10) {
      return '0' + time;
    } else {
      return '' + time;
    }
  }

  private prepareData(contact: ICompanyContact): void {
    if (!contact) return;

    if (!contact.Schedules) contact.Schedules = [];

    if (!contact.Schedules.length) return;

    const schedule = contact.Schedules;
    let thisSchedule: IScheduleDay;
    let theseTimes: IScheduleTime[];

    for (let i = 0; i < schedule.length; i++) {
      thisSchedule = schedule[i];
      theseTimes = thisSchedule.Times;

      this.daysMap[thisSchedule.Day].Selected = true;
      thisSchedule.LongName = this.daysMap[thisSchedule.Day].LongName;
      thisSchedule.ShortName = this.daysMap[thisSchedule.Day].ShortName;

      if ((theseTimes.length === 1) && (theseTimes[0].From === null) && (theseTimes[0].To === null)) {
        thisSchedule.AllDay = true;
      }
    }
  }

  private onContactChanged(): void {
    this.contactChanged.emit(this.contact);
  }

  private selectDefaultTimeZone(): void {
    const timeZoneOffset = new Date().getTimezoneOffset() / -60;
    let timeZoneAbbr: string;

    switch (timeZoneOffset) {
      case -10:
        timeZoneAbbr = 'HT';
        break;
      case -9:
        timeZoneAbbr = 'AT';
        break;
      case -8:
        timeZoneAbbr = 'PT';
        break;
      case -7:
        timeZoneAbbr = 'MT';
        break;
      case -6:
        timeZoneAbbr = 'CT';
        break;
      case -5:
        timeZoneAbbr = 'ET';
        break;
    }

    for (let i = 0; i < this.timeZones.length; i++) {
      const timeZone = this.timeZones[i];
      if (timeZone.TimeZoneAbbr === timeZoneAbbr) {
        this.contact.TimeZoneId = timeZone.TimeZoneId;
        break;
      }
    }
  }

  addScheduleGroup(): void {
    const days = this.days.map(d => d.Day); // Extract day values (e.g., 'M', 'T', etc.)
    const unselectedDays: { [key: string]: boolean } = {};
    const blankIds: { [key: string]: any } = {};
  
    days.forEach(day => {
      unselectedDays[day] = false;
      blankIds[day] = null;
    });
  
    this.contact.GroupedSchedules.push({
      CompanyContactId: null,
      CompanyContactScheduleId: null,
      ScheduleDay: null,
      ScheduleDayNum: null,
      ScheduleTimeFrom: null,
      ScheduleTimeTo: null,
      AllDay: true,
      DaySelected: unselectedDays,
      HasId: blankIds,
      isDeleted: false
    });
  
    this.onContactChanged(); // Emit the updated contact
  }

  addTimeToDay(day: IWeekDay, time: IScheduleTime = { From: '08:00:00', To: '17:00:00' }): void {
    let currentSchedule: IScheduleDay = null;
    let i: number;

    for (i = 0; i < this.contact.Schedules.length; i++) {
      if (this.contact.Schedules[i].Day === day.Day) {
        currentSchedule = this.contact.Schedules[i];
        break;
      }
      if (this.daysMap[this.contact.Schedules[i].Day].SortOrder > this.daysMap[day.Day].SortOrder) break;
    }

    if (!currentSchedule) {
      this.contact.Schedules.splice(i, 0, (currentSchedule = {
        Day: day.Day,
        LongName: this.daysMap[day.Day].LongName,
        ShortName: this.daysMap[day.Day].ShortName,
        Times: [],
        AllDay: true
      }));
      currentSchedule.Times = [{
        From: null,
        To: null
      }];
    } else if (!currentSchedule.Times) {
      currentSchedule.Times = [{
          From: '08:00:00',
          To: '17:00:00'
        }];
    } else {
      currentSchedule.Times.push(time);
    }
    this.onContactChanged();
  }

  allDayClicked(group: IScheduleGroup): void {
    if (!group.AllDay) {
      group.ScheduleTimeFrom = '08:00:00';
      group.ScheduleTimeTo = '17:00:00';
    } else {
      group.ScheduleTimeFrom = null;
      group.ScheduleTimeTo = null;
    }
    this.onContactChanged();
  }

  timeToString(time: any): string {
    if (typeof(time) === 'string') {
      const timeParts = time.split(':');
      time = new Date(0, 0, 0, parseInt(timeParts[0], 10), parseInt(timeParts[1], 10), 0);
    }
    const hours = time.getHours() === 0 ? 24 : time.getHours();
    const isPM = hours >= 12 && hours < 24;
    const minutes = (time.getMinutes() < 10 ? '0' : '') + time.getMinutes();
    return `${ hours === 12 || hours === 24 ? 12 : isPM ? hours - 12 : hours }:${ minutes } ${ isPM ? 'PM' : 'AM' }`;
  }

  ngOnInit() {
    if (this.contact) this.prepareData(this.contact);

    this.generateTimeOptions();

    this.common.getTimeZones().subscribe(timeZones => {
      this.timeZones = timeZones;
      if (!this.contact.TimeZoneId) this.selectDefaultTimeZone();
    });
  }
}

interface IWeekDay {
  SortOrder: number;
  ShortName: string;
  LongName: string;
  Day: string;
  Selected: boolean;
}



// WEBPACK FOOTER //
// ./src/app/components/common/availabilitySelector/availabilitySelector.component.ts